/**
 * useAnalysisMode Hook
 * 
 * Handles Analysis Mode (Westworld-style debugging):
 * - Enter/exit analysis mode state
 * - Function call handling
 * - Callback management for external handlers
 * 
 * XSTATE MIGRATION: Now dispatches events to state machine instead of direct setState.
 */

import { useCallback, useRef } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { logger } from '../../utils/logger';
import type { TranscriptMessage, AnalysisModeCallbacks, SendFunction } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseAnalysisModeParams {
  send: SendFunction;
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  systemInstructionsRef: React.MutableRefObject<string>;
}

export interface UseAnalysisModeReturn {
  enterAnalysisMode: () => void;
  exitAnalysisMode: () => void;
  sendFunctionResult: (callId: string, result: string | object, autoRespond?: boolean, responseToolChoice?: 'auto' | 'none' | 'required' | { type: 'function'; name: string }) => void;
  setAnalysisModeCallbacks: (callbacks: AnalysisModeCallbacks) => void;
  getCurrentInstructions: () => string | null;
  
  // Handler for function calls from WebSocket
  handleFunctionCall: (callId: string, name: string, args: Record<string, any>) => void;
  
  // Callback refs (for use by orchestrator)
  analysisModeEnterRef: React.MutableRefObject<((messages: TranscriptMessage[]) => void | Promise<void>) | null>;
  analysisModeExitRef: React.MutableRefObject<(() => void | Promise<void>) | null>;
  analysisFunctionCallRef: React.MutableRefObject<((callId: string, name: string, args: Record<string, any>) => void | Promise<void>) | null>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAnalysisMode({
  send,
  websocketServiceRef,
  systemInstructionsRef,
}: UseAnalysisModeParams): UseAnalysisModeReturn {
  
  // Callback refs for external handlers
  const analysisModeEnterRef = useRef<((messages: TranscriptMessage[]) => void | Promise<void>) | null>(null);
  const analysisModeExitRef = useRef<(() => void | Promise<void>) | null>(null);
  const analysisFunctionCallRef = useRef<((callId: string, name: string, args: Record<string, any>) => void | Promise<void>) | null>(null);

  // Enter analysis mode
  const enterAnalysisMode = useCallback(() => {
    logger.info('[WebSocket] 🔬 Entering Analysis Mode');
    // Dispatch event to state machine
    send({ type: 'ENTER_ANALYSIS_MODE' });
    // The actual prompt switch is handled by the onAnalysisModeEnter callback
  }, [send]);

  // Exit analysis mode
  const exitAnalysisMode = useCallback(() => {
    logger.info('[WebSocket] 🔬 Exiting Analysis Mode');
    // Dispatch event to state machine
    send({ type: 'EXIT_ANALYSIS_MODE' });
    // The actual prompt switch is handled by the onAnalysisModeExit callback
  }, [send]);

  // Send function result back to OpenAI
  const sendFunctionResult = useCallback((callId: string, result: string | object, autoRespond?: boolean, responseToolChoice?: 'auto' | 'none' | 'required' | { type: 'function'; name: string }) => {
    const wsService = websocketServiceRef.current;
    if (wsService) {
      wsService.sendFunctionResult(callId, result, autoRespond, responseToolChoice);
    } else {
      console.warn('[WebSocket] Cannot send function result - no WebSocket service');
    }
  }, [websocketServiceRef]);

  // Set callbacks for analysis mode events
  const setAnalysisModeCallbacks = useCallback((callbacks: AnalysisModeCallbacks) => {
    analysisModeEnterRef.current = callbacks.onEnter || null;
    analysisModeExitRef.current = callbacks.onExit || null;
    analysisFunctionCallRef.current = callbacks.onFunctionCall || null;
  }, []);

  // Get current system instructions
  const getCurrentInstructions = useCallback((): string | null => {
    return systemInstructionsRef.current || null;
  }, [systemInstructionsRef]);

  // Handle function call from WebSocket
  const handleFunctionCall = useCallback((callId: string, name: string, args: Record<string, any>) => {
    logger.debug('[WebSocket] 🔬 Function call received:', { callId, name, hasArgs: Boolean(args) });
    
    // Set processing state
    send({ type: 'SET_ANALYSIS_PROCESSING', processing: true });
    
    // Forward to external handler via callback ref
    if (analysisFunctionCallRef.current) {
      const result = analysisFunctionCallRef.current(callId, name, args);
      if (result instanceof Promise) {
        result
          .then(() => {
            send({ type: 'SET_ANALYSIS_PROCESSING', processing: false });
          })
          .catch((error) => {
            console.error('[WebSocket] 🔬 Async function call handler error:', error);
            send({ type: 'SET_ANALYSIS_PROCESSING', processing: false });
          });
      } else {
        send({ type: 'SET_ANALYSIS_PROCESSING', processing: false });
      }
    } else {
      send({ type: 'SET_ANALYSIS_PROCESSING', processing: false });
    }
  }, [send]);

  return {
    enterAnalysisMode,
    exitAnalysisMode,
    sendFunctionResult,
    setAnalysisModeCallbacks,
    getCurrentInstructions,
    handleFunctionCall,
    analysisModeEnterRef,
    analysisModeExitRef,
    analysisFunctionCallRef,
  };
}
